/**
 * The named theme catalog.
 *
 * A theme is a PAIR — a light half and a dark half — and that is the whole
 * reason this is a catalog rather than more entries in the mode list. Light and
 * dark stop being themes and become the choice of WHICH HALF paints, shared
 * across every theme in here. `null` is a legal half: a theme may ship only one.
 *
 * Each half is a {@link ThemeColorSeed}, so a named theme and a user's custom
 * theme resolve through the exact same solver and inherit the same guarantees.
 * Nothing here is a literal token set — three source colors plus two optional
 * character axes go in, and `resolveCustomTheme` solves the rest against the
 * APCA floors. That is what keeps a palette from shipping illegible, and it is
 * why importing a reference theme does NOT mean copying its forty values.
 *
 * `secondary` and `accent` are deliberately NOT carried over from a source
 * palette. Here they are the neutral hover and selected rungs; expressive
 * palettes repurpose them as brand fills, and adopting that would make every
 * hover read as a selection.
 *
 * Seeds derived from the tweakcn theme registry (https://tweakcn.com), which
 * publishes shadcn-format themes. Titles of the brand-derived entries were
 * changed — the keys are persisted identifiers and must never change, but the
 * titles are what a user reads, and shipping another company's mark as a
 * product feature is not ours to do. See `THIRD_PARTY_NOTICES.md`.
 */

import type { ThemeColorSeed, ThemeOverrides } from "./custom-theme.ts";

/**
 * A palette's SHAPE: how round its corners are and how its shadows are cast.
 *
 * Parameters, not finished shadows — the same reason the color half stores
 * seeds. A palette says what its shadow is MADE of (how far it is thrown, how
 * far it spreads, how hard it lands); the surface roles here say what a shadow
 * MEANS (a card rests, an elevated panel floats, a well casts nothing). Import
 * the character, keep the structure, and a brutalist hard-offset shadow stays
 * hard at every rung instead of flattening the elevation ladder.
 */
export interface ThemeShadow {
  color: string;
  opacity: number;
  blur: string;
  spread: string;
  offsetX: string;
  offsetY: string;
}

export interface NamedTheme {
  /** Stable identifier. PERSISTED in preferences — never rename one. */
  key: string;
  /** What a user reads. Safe to change. */
  title: string;
  light: ThemeColorSeed | null;
  dark: ThemeColorSeed | null;
  /**
   * Values this palette states outright rather than having solved, per half.
   *
   * Adds no token to the system — it moves where a handful of existing ones get
   * their value. Recorded only for tokens whose authored value differs enough
   * from the solved one to be worth carrying AND still clears every APCA floor
   * once applied; the generator drops the rest, so anything in here is known
   * good. Where a palette says nothing, the solver still answers.
   */
  overrides?: { light?: ThemeOverrides; dark?: ThemeOverrides };
  /** Corner radius. One value: the whole scale derives from it. */
  radius?: string;
  /** Shadow character per half — eight of the 36 cast differently by mode. */
  shadow?: { light: ThemeShadow | null; dark: ThemeShadow | null };
  /**
   * Kept out of every picker while staying a live key: a stored preference
   * naming it still resolves and paints, `themeSeedFor` still answers it, and
   * only the featured/unfeatured partitions the pickers enumerate skip it.
   * For palettes kept as a reference or an escape hatch — `legacy-default`
   * below — rather than as an offer.
   */
  hidden?: boolean;
}

/**
 * What each surface's shadow is worth, as a multiple of the palette's own.
 *
 * The ladder our design already runs, expressed as scalars so a palette can
 * supply the character without supplying the hierarchy. Zero really is zero: a
 * well and a sidebar cast nothing at any setting, and a palette whose blur or
 * opacity is 0 stays flat at every rung — which is what keeps a hard-offset
 * brutalist shadow brutalist instead of blurring it into a soft one.
 */
const SHADOW_ROLE: Record<string, number> = {
  sunken: 0,
  sidebar: 0,
  background: 0.6,
  card: 1,
  control: 1,
  popover: 1.6,
  elevated: 2.4,
};

/** The CSS a surface's shadow token takes under a palette's shape. */
export function themeShadowCss(shadow: ThemeShadow, surface: string): string {
  const scale = SHADOW_ROLE[surface] ?? 1;
  if (scale === 0 || shadow.opacity === 0) return "none";
  const px = (value: string, by: number): string => {
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? `${Math.round(n * by * 100) / 100}px` : value;
  };
  const alpha = Math.min(1, shadow.opacity * scale);
  return [
    shadow.offsetX,
    px(shadow.offsetY, scale),
    px(shadow.blur, scale),
    shadow.spread,
    `color-mix(in oklab, ${shadow.color} ${Math.round(alpha * 100)}%, transparent)`,
  ].join(" ");
}

/** Every shadow token a shape writes, so applying and clearing stay in step. */
export const THEME_SHADOW_SURFACES = Object.keys(SHADOW_ROLE);

/* eslint-disable no-restricted-syntax -- A seed IS a literal source color: it
   is the INPUT the token system solves from, so resolving one from a semantic
   token would make the catalog depend on its own output. Same reason
   `DEFAULT_THEME_COLOR_SEEDS` is exempt. Nothing here is painted directly —
   every value a component sees comes out of `resolveCustomTheme`. */
export const THEME_CATALOG: readonly NamedTheme[] = [
  // BEGIN generated — scripts/import-theme-catalog.ts
  // Source: ChatGPT/Codex desktop presets. Rebuild with
  // scripts/import-openai-catalog.ts; scripts/import-theme-catalog.ts
  // restores the tweakcn set into this same block.
  {
    key: "gpt-absolutely",
    title: "Absolutely",
    light: { background: "#f9f9f7", foreground: "#2d2d2b", accent: "#cc7d5e" },
    dark: { background: "#2d2d2b", foreground: "#f9f9f7", accent: "#cc7d5e" },
  },
  {
    key: "gpt-ayu",
    title: "Ayu",
    light: null,
    dark: { background: "#10141c", foreground: "#bfbdb6", accent: "#e6b450" },
  },
  {
    key: "gpt-catppuccin",
    title: "Catppuccin",
    light: { background: "#eff1f5", foreground: "#4c4f69", accent: "#1e66f5" },
    dark: { background: "#1e1e2e", foreground: "#cdd6f4", accent: "#89b4fa" },
  },
  {
    key: "gpt-codex",
    title: "Codex",
    light: { background: "#ffffff", foreground: "#0d0d0d", accent: "#0169cc" },
    dark: { background: "#111111", foreground: "#fcfcfc", accent: "#0169cc" },
  },
  {
    key: "gpt-dracula",
    title: "Dracula",
    light: null,
    dark: { background: "#282a36", foreground: "#f8f8f2", accent: "#6272a4" },
  },
  {
    key: "gpt-everforest",
    title: "Everforest",
    light: { background: "#fdf6e3", foreground: "#5c6a72", accent: "#8da101" },
    dark: { background: "#2d353b", foreground: "#d3c6aa", accent: "#a7c080" },
  },
  {
    key: "gpt-github",
    title: "GitHub",
    light: { background: "#ffffff", foreground: "#1f2328", accent: "#0969da" },
    dark: { background: "#0d1117", foreground: "#e6edf3", accent: "#2f81f7" },
  },
  {
    key: "gpt-gruvbox",
    title: "Gruvbox",
    light: { background: "#fbf1c7", foreground: "#3c3836", accent: "#076678" },
    dark: { background: "#282828", foreground: "#ebdbb2", accent: "#83a598" },
  },
  {
    key: "gpt-linear",
    title: "Linear",
    light: { background: "#fcfcfd", foreground: "#1b1b1b", accent: "#5e6ad2" },
    dark: { background: "#0f0f11", foreground: "#e3e4e6", accent: "#606acc" },
  },
  {
    key: "gpt-lobster",
    title: "Lobster",
    light: null,
    dark: { background: "#111827", foreground: "#e4e4e7", accent: "#ff5c5c" },
  },
  {
    key: "gpt-material",
    title: "Material",
    light: null,
    dark: { background: "#212121", foreground: "#eeffff", accent: "#80cbc4" },
  },
  {
    key: "gpt-matrix",
    title: "Matrix",
    light: null,
    dark: { background: "#040805", foreground: "#b8ffca", accent: "#1eff5a" },
  },
  {
    key: "gpt-monokai",
    title: "Monokai",
    light: null,
    dark: { background: "#272822", foreground: "#f8f8f2", accent: "#99947c" },
  },
  {
    key: "gpt-night-owl",
    title: "Night Owl",
    light: null,
    dark: { background: "#011627", foreground: "#d6deeb", accent: "#122d42" },
  },
  {
    key: "gpt-nord",
    title: "Nord",
    light: null,
    dark: { background: "#2e3440", foreground: "#d8dee9", accent: "#88c0d0" },
  },
  {
    key: "gpt-notion",
    title: "Notion",
    light: { background: "#ffffff", foreground: "#37352f", accent: "#3183d8" },
    dark: { background: "#191919", foreground: "#d9d9d8", accent: "#3183d8" },
  },
  {
    key: "gpt-one",
    title: "One",
    light: { background: "#fafafa", foreground: "#383a42", accent: "#526fff" },
    dark: { background: "#282c34", foreground: "#bdbfc1", accent: "#61afef" },
  },
  {
    key: "gpt-oscurange",
    title: "Oscurange",
    light: null,
    dark: { background: "#0b0b0f", foreground: "#e6e6e6", accent: "#f9b98c" },
  },
  {
    key: "gpt-proof",
    title: "Proof",
    light: { background: "#f5f3ed", foreground: "#2f312d", accent: "#3d755d" },
    dark: null,
  },
  {
    key: "gpt-raycast",
    title: "Raycast",
    light: { background: "#ffffff", foreground: "#030303", accent: "#ff6363" },
    dark: { background: "#101010", foreground: "#fefefe", accent: "#ff6363" },
  },
  {
    key: "gpt-rose-pine",
    title: "Rosé Pine",
    light: { background: "#faf4ed", foreground: "#575279", accent: "#907aa9" },
    dark: { background: "#232136", foreground: "#e0def4", accent: "#c4a7e7" },
  },
  {
    key: "gpt-sentry",
    title: "Sentry",
    light: null,
    dark: { background: "#2d2935", foreground: "#e6dff9", accent: "#7055f6" },
  },
  {
    key: "gpt-solarized",
    title: "Solarized",
    light: { background: "#fdf6e3", foreground: "#67645b", accent: "#b49471" },
    dark: { background: "#002b36", foreground: "#b2bec1", accent: "#2aa198" },
  },
  {
    key: "gpt-temple",
    title: "Temple",
    light: null,
    dark: { background: "#02120c", foreground: "#c7e6da", accent: "#e4f222" },
  },
  {
    key: "gpt-tokyo-night",
    title: "Tokyo Night",
    light: null,
    dark: { background: "#1a1b26", foreground: "#b7b7bc", accent: "#6183bb" },
  },
  {
    key: "gpt-vercel",
    title: "Vercel",
    light: { background: "#ffffff", foreground: "#171717", accent: "#006aff" },
    dark: { background: "#000000", foreground: "#ededed", accent: "#006efe" },
  },
  {
    key: "gpt-vscode-plus",
    title: "VS Code",
    light: { background: "#ffffff", foreground: "#000000", accent: "#007acc" },
    dark: { background: "#1e1e1e", foreground: "#d4d4d4", accent: "#007acc" },
  },
  {
    key: "gpt-xcode",
    title: "Xcode",
    light: { background: "#ffffff", foreground: "#000000", accent: "#0e0eff" },
    dark: { background: "#1f1f24", foreground: "#ffffff", accent: "#5482ff" },
  },
  // END generated
  {
    // The three hand-authored neutrals — `graphite`, `slate`, `stone` — share
    // one grammar and differ only in the KIND of neutral: pure grey, cool
    // blue-grey, warm brown-grey. Each keeps the shipped neutral primary and
    // blue brand ink (the accent is the default's), sits the contrast dial
    // just above neutral for crisper hairlines, and opens the dark ladder a
    // step (`depth`) so column, card and page read as distinct planes. The
    // tint, where there is one, is never stated: it is the ink's own chroma,
    // which `cast()` carries into every derived plane at reference strength
    // (~0.01 in the dark rungs, what Linear and Notion ship).
    //
    // Graphite: chroma 0 — the default's neutral drawn sharper. Pure white
    // page under near-black ink; deeper charcoal night under a brighter ink.
    // The ChatGPT / Vercel / Raycast family, and the default-retune candidate.
    key: "graphite",
    title: "Graphite",
    light: {
      background: "#ffffff",
      foreground: "#111111",
      accent: "#3168dd",
      neutralPrimary: true,
      contrast: 60,
    },
    dark: {
      background: "#161616",
      foreground: "#ededed",
      accent: "#77a3f9",
      neutralPrimary: true,
      contrast: 60,
      depth: 1.15,
    },
  },
  {
    // Slate: the COOL neutral. White page, but the ink is a deep slate navy
    // and every solved plane inherits its cast — a cool shell in light, a
    // blue-black night. The Linear-dark / Attio / Jira / shadcn-slate family.
    key: "slate",
    title: "Slate",
    light: {
      background: "#ffffff",
      foreground: "#0f172a",
      accent: "#3168dd",
      neutralPrimary: true,
      contrast: 58,
    },
    dark: {
      background: "#12151b",
      foreground: "#dfe3ea",
      accent: "#77a3f9",
      neutralPrimary: true,
      contrast: 58,
      depth: 1.1,
    },
  },
  {
    // Stone: the WARM neutral. A cream-white page under brown-grey ink; the
    // dark page IS the light ink, so the two halves share one material. The
    // coss / Cal.com stone, Notion-ink, Asana-shell family.
    key: "stone",
    title: "Stone",
    light: {
      background: "#faf9f7",
      foreground: "#1c1917",
      accent: "#3168dd",
      neutralPrimary: true,
      contrast: 58,
    },
    dark: {
      background: "#1c1917",
      foreground: "#e7e5e4",
      accent: "#77a3f9",
      neutralPrimary: true,
      contrast: 58,
      depth: 1.1,
    },
  },
  {
    // The dark default as it shipped before the Notion-ground retune (page
    // #121212, ink #e5e5e5) — kept so the old design stays one key away when
    // tuning the new one, not offered as a choice. Apply it by storing the
    // key (`theme.dark = "legacy-default"`); no picker lists it. The light
    // half was not retuned, so this ships none and light falls back to the
    // shipped design.
    key: "legacy-default",
    title: "Legacy Default",
    light: null,
    dark: {
      background: "#121212",
      foreground: "#e5e5e5",
      accent: "#77a3f9",
      neutralPrimary: true,
    },
    hidden: true,
  },
] as const;
/* eslint-enable no-restricted-syntax */

/**
 * Catalog keys hoisted out of the long list into the picker's first group.
 *
 * The shipped design and the user's own theme are always up there — those are
 * positions, not entries, so they are not named here. This is for catalog
 * palettes worth surfacing ahead of an alphabet: add a key and it moves up and
 * out of the main group in one edit, with nothing to keep in sync.
 */
export const FEATURED_THEME_KEYS: readonly string[] = ["graphite", "slate", "stone"];

/**
 * The hoisted palettes, in the order the key list gives them.
 *
 * The list is a parameter so the partition can be exercised and reasoned about
 * without the shipped one having to be non-empty — which would mean inventing
 * favorites to satisfy a test. A key naming no catalog entry is skipped rather
 * than throwing: the list is editorial, and a withdrawn palette should not take
 * the picker down with it.
 */
export const featuredThemes = (
  keys: readonly string[] = FEATURED_THEME_KEYS,
): readonly NamedTheme[] =>
  keys.flatMap((key) => {
    const theme = THEME_CATALOG.find((t) => t.key === key);
    return theme && !theme.hidden ? [theme] : [];
  });

/** Everything not hoisted. The two partitions cover the PICKABLE catalog —
 *  every non-hidden entry — exactly once; a hidden entry is in neither. */
export const unfeaturedThemes = (
  keys: readonly string[] = FEATURED_THEME_KEYS,
): readonly NamedTheme[] => {
  const featured = new Set(keys);
  return THEME_CATALOG.filter(
    (theme) => !featured.has(theme.key) && !theme.hidden,
  );
};


const BY_KEY = new Map(THEME_CATALOG.map((theme) => [theme.key, theme]));

/** A palette's stated values for a mode, or undefined where it states none. */
export function namedThemeOverrides(
  key: string | null | undefined,
  mode: "light" | "dark",
): ThemeOverrides | undefined {
  return key == null ? undefined : namedTheme(key)?.overrides?.[mode];
}

/** The catalog entry for a key, or null. */
export function namedTheme(key: string | null | undefined): NamedTheme | null {
  return key == null ? null : (BY_KEY.get(key) ?? null);
}

/** Whether a string names a catalog theme. The only sanctioned narrowing. */
export function isNamedThemeKey(
  value: string | null | undefined,
): value is string {
  return value != null && BY_KEY.has(value);
}

/**
 * The seed a theme paints in a mode, or `null` when it ships no such half.
 * A null half is not an error — the caller falls back to the shipped design,
 * which is exactly what an unauthored custom half does.
 */
export function namedThemeSeed(
  key: string | null | undefined,
  mode: "light" | "dark",
): ThemeColorSeed | null {
  if (key == null) return null;
  return BY_KEY.get(key)?.[mode] ?? null;
}
